import { useQuery } from '@apollo/client'
import moment from 'moment'
import React from 'react'
import { Badge, Button, Form, Spinner } from 'react-bootstrap'
import { GET_CONTACT_COMMUNICATION_LOGS } from '../../../gql/queries/contactsApiQueries'
import { useDisclourse } from '../../../hooks/useDisclourse'
import { SendMessageCanvas } from '../../../components/messaging/SendMessageCanvas'
import { URL as BACKEND_URL } from '../../../gql/client'
import { STORAGE_NAMES } from '../../../helpers/consts'

interface VicidialCallRecordingPlayerProps {
  callId: number | string
  recordingId?: string
}

const VicidialCallRecordingPlayer: React.FC<VicidialCallRecordingPlayerProps> = ({ callId, recordingId }) => {
  const [url, setUrl] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const loadRecording = async () => {
    if (url || loading) return
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
      const response = await fetch(`${BACKEND_URL}/api/telephony/calls/${callId}/recording`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data?.error || 'Recording not found')
      }
      if (!data?.url) {
        throw new Error('Recording URL missing')
      }
      setUrl(data.url)
    } catch (exc: any) {
      setError(exc?.message || 'Unable to load recording')
    } finally {
      setLoading(false)
    }
  }

  if (url) {
    return (
      <div className='mt-1'>
        <audio controls preload='none' src={url} style={{ width: '100%', maxWidth: '320px', height: '32px' }} />
      </div>
    )
  }

  return (
    <div className='mt-1 d-flex align-items-center gap-2'>
      <Button
        variant='outline-primary'
        size='sm'
        onClick={loadRecording}
        disabled={loading}
        className='d-inline-flex align-items-center gap-1'
      >
        {loading ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <i className='bi bi-play-circle' />
        )}
        <span>{loading ? 'Loading...' : 'Play recording'}</span>
      </Button>
      {recordingId && <span className='text-muted small'>#{recordingId}</span>}
      {error && <span className='text-danger small'>{error}</span>}
    </div>
  )
}

interface ICommunicationsTabProps {
  originModule: 'quotes' | 'policies' | 'leads'
  originId: string
  contactId?: string
  recipient?: {
    name: string
    phone: string
    email?: string
  }
  contextData?: any
}

export const CommunicationsTab: React.FC<ICommunicationsTabProps> = ({
  originModule,
  originId,
  contactId,
  recipient,
  contextData,
}) => {
  const { isOpen: isOpenMessaging, onOpen: onOpenMessaging, onClose: onCloseMessaging } = useDisclourse()
  const historyContainerRef = React.useRef<HTMLDivElement | null>(null)

  const normalizedOriginModule = React.useMemo(() => {
    switch (originModule) {
      case 'policies':
      case 'quotes':
        // Show both Policy and Quote history combined
        return 'policy,quote'
      case 'leads':
        return 'lead'
      default:
        return originModule
    }
  }, [originModule])

  const {
    data: objectData,
    loading: loadingObjectLogs,
    error: objectError,
    refetch: refetchObjectLogs,
  } = useQuery(GET_CONTACT_COMMUNICATION_LOGS, {
    variables: {
      originModule: normalizedOriginModule,
      originId,
    },
    skip: !originId,
    fetchPolicy: 'network-only',
  })

  const {
    data: contactData,
    loading: loadingContactLogs,
    error: contactError,
    refetch: refetchContactLogs,
  } = useQuery(GET_CONTACT_COMMUNICATION_LOGS, {
    variables: { contactId },
    skip: !contactId,
    fetchPolicy: 'network-only',
  })

  const [selectedChannels, setSelectedChannels] = React.useState<string[]>(['WHATSAPP', 'SMS', 'EMAIL', 'CALL'])
  const logs = React.useMemo(() => {
    const merged = new Map<string, any>()
    const allEdges = [
      ...(objectData?.allCommunications?.edges || []),
      ...(contactData?.allCommunications?.edges || []),
    ]

    allEdges.forEach((edge: any) => {
      if (!edge?.node?.id || merged.has(edge.node.id)) return
      // Parse metadata if it's a JSON string
      if (edge.node.metadata && typeof edge.node.metadata === 'string') {
        try { edge = { ...edge, node: { ...edge.node, metadata: JSON.parse(edge.node.metadata) } } } catch {}
      }
      merged.set(edge.node.id, edge)
    })

    return Array.from(merged.values())
  }, [objectData, contactData])

  const loading = loadingObjectLogs || loadingContactLogs
  const error = objectError || contactError

  const refetch = React.useCallback(async () => {
    await Promise.all([
      refetchObjectLogs(),
      contactId ? refetchContactLogs() : Promise.resolve(),
    ])
  }, [contactId, refetchContactLogs, refetchObjectLogs])

  const filteredAndSortedLogs = React.useMemo(() => {
    return [...logs]
      .filter(({ node }: any) => {
        const channel = node.channel?.toUpperCase() || 'OTHER'
        return selectedChannels.includes(channel)
      })
      .sort((a: any, b: any) => {
        return new Date(a.node.created).getTime() - new Date(b.node.created).getTime()
      })
  }, [logs, selectedChannels])

  React.useEffect(() => {
    const container = historyContainerRef.current
    if (!container || filteredAndSortedLogs.length === 0) return
    container.scrollTop = container.scrollHeight
  }, [filteredAndSortedLogs])

  const toggleChannel = (channel: string) => {
    if (selectedChannels.includes(channel)) {
      setSelectedChannels(selectedChannels.filter((c) => c !== channel))
    } else {
      setSelectedChannels([...selectedChannels, channel])
    }
  }

  const getStatusBadge = (status: string) => {
    const s = status.toUpperCase()
    if (s === 'SENT') return 'primary'
    if (s === 'DELIVERED') return 'info'
    if (s === 'READ') return 'success'
    if (s === 'FAILED') return 'danger'
    if (s === 'RECEIVED') return 'warning'
    if (s === 'COMPLETED') return 'success'
    return 'secondary'
  }

  const getChannelIcon = (channel: string) => {
    const c = channel.toUpperCase()
    if (c === 'WHATSAPP') return 'bi-whatsapp'
    if (c === 'SMS') return 'bi-chat-dots'
    if (c === 'EMAIL') return 'bi-envelope'
    if (c === 'CALL') return 'bi-telephone'
    return 'bi-chat'
  }

  const formatCallDuration = (seconds: number) => {
    if (!seconds) return '0s'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`
  }

  if (loading) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center p-10'>
        <Spinner animation='border' variant='primary' />
        <span className='mt-2 text-muted'>Loading communications...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className='alert alert-danger'>
        <i className='bi bi-exclamation-triangle-fill me-2'></i>
        Error loading communications: {error.message}
      </div>
    )
  }

  return (
    <div className='bg-white p-6 rounded border'>
      <div className='d-flex flex-wrap justify-content-between align-items-center mb-6 gap-3'>
        <div className='d-flex align-items-center gap-4'>
          <h3 className='m-0 me-2'>Communication History</h3>
          <div className='d-flex align-items-center gap-3 bg-light px-4 py-2 rounded-pill border'>
            <span className='text-muted fw-bold small text-uppercase me-1'>Filter:</span>
            <Form.Check
              type='checkbox'
              id='filter-whatsapp'
              label={<><i className='bi bi-whatsapp success ms-1'></i> WhatsApp</>}
              checked={selectedChannels.includes('WHATSAPP')}
              onChange={() => toggleChannel('WHATSAPP')}
              className='small fw-bold'
            />
            <Form.Check
              type='checkbox'
              id='filter-sms'
              label={<><i className='bi bi-chat-dots ms-1 text-primary'></i> SMS</>}
              checked={selectedChannels.includes('SMS')}
              onChange={() => toggleChannel('SMS')}
              className='small fw-bold'
            />
            <Form.Check
              type='checkbox'
              id='filter-call'
              label={<><i className='bi bi-telephone ms-1 text-info'></i> Calls</>}
              checked={selectedChannels.includes('CALL')}
              onChange={() => toggleChannel('CALL')}
              className='small fw-bold'
            />
          </div>
        </div>
        <div className='d-flex gap-2'>
          <Button variant='outline-secondary' size='sm' onClick={() => refetch()} title="Refresh History">
            <i className='bi bi-arrow-clockwise'></i>
          </Button>
          {recipient && (
            <Button variant='primary' size='sm' onClick={onOpenMessaging}>
              <i className='bi bi-send me-2'></i>
              Send Message
            </Button>
          )}
        </div>
      </div>

      {logs.length === 0 ? (
        <div className='text-center p-10 py-15'>
          <i className='bi bi-chat-left-text text-muted fs-1 mb-3 d-block'></i>
          <p className='text-muted'>No communication logs found.</p>
        </div>
      ) : (
        <div
          ref={historyContainerRef}
          className='d-flex flex-column gap-4 pe-2'
          style={{
            maxHeight: '70vh',
            minHeight: '28rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollBehavior: 'smooth',
          }}
        >
          {filteredAndSortedLogs.map(({ node }: any) => {
            const isOutbound = node.direction === 'OUTBOUND'
            return (
              <div
                key={node.id}
                className={`d-flex flex-column ${isOutbound ? 'align-items-end' : 'align-items-start'}`}
              >
                <div
                  className={`cardbox-message`}
                  style={{
                    maxWidth: '80%',
                    backgroundColor: node.channel?.toUpperCase() === 'CALL'
                      ? (isOutbound ? '#E8F0FE' : '#FFF3E0')
                      : node.channel?.toUpperCase() === 'WHATSAPP'
                        ? (isOutbound ? '#DCF8C6' : '#E7F3EF')
                        : (isOutbound ? '#F2F2F2' : '#FFFFFF'),
                    borderColor: node.channel?.toUpperCase() === 'CALL'
                      ? (isOutbound ? '#B3D4FC' : '#FFD9A0')
                      : node.channel?.toUpperCase() === 'WHATSAPP'
                        ? (isOutbound ? '#C7E9A0' : '#C1D6D0')
                        : (isOutbound ? '#D1D1D1' : '#E0E0E0'),
                    borderBottomRightRadius: isOutbound ? '0' : '8px',
                    borderBottomLeftRadius: isOutbound ? '8px' : '0',
                  }}
                >
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <i className={`bi ${getChannelIcon(node.channel)} ${node.channel?.toUpperCase() === 'WHATSAPP' ? 'text-success' : node.channel?.toUpperCase() === 'CALL' ? 'text-info' : 'text-primary'}`} />
                    <span className='fw-bold x-small text-uppercase'>{node.channel}</span>
                    <Badge bg={getStatusBadge(node.status)} className='x-small px-2' style={{ fontSize: '0.65rem' }}>
                      {node.status}
                    </Badge>
                  </div>

                  {node.channel?.toUpperCase() === 'CALL' ? (
                    <div className='small'>
                      <div className='d-flex flex-wrap gap-3 mb-1'>
                        {node.metadata?.disposition && (
                          <span><strong>Disposition:</strong> <Badge bg='warning' text='dark' className='ms-1'>{node.metadata.disposition}</Badge></span>
                        )}
                        {node.metadata?.talk_time_seconds > 0 && (
                          <span><strong>Duration:</strong> {formatCallDuration(node.metadata.talk_time_seconds)}</span>
                        )}
                        {node.metadata?.term_reason && (
                          <span><strong>Ended by:</strong> {node.metadata.term_reason}</span>
                        )}
                      </div>
                      {node.metadata?.phone && (
                        <div className='text-muted'><i className='bi bi-telephone me-1'></i>{node.metadata.phone}</div>
                      )}
                      {node.metadata?.call_id && (
                        <VicidialCallRecordingPlayer
                          callId={node.metadata.call_id}
                          recordingId={node.metadata?.recording_id}
                        />
                      )}
                      {node.content && !node.content.startsWith('Call ') && (
                        <div className='mt-1 text-muted fst-italic'>{node.content}</div>
                      )}
                    </div>
                  ) : (
                    <div className='small text-break' style={{ whiteSpace: 'pre-wrap' }}>
                      {node.content || <span className='fst-italic text-muted'>No content</span>}
                    </div>
                  )}

                  {node.metadata?.media_urls && Array.isArray(node.metadata.media_urls) && node.metadata.media_urls.length > 0 && (
                    <div className='mt-2 d-flex flex-column gap-1'>
                      {node.metadata.media_urls.map((url: string, idx: number) => {
                        const isTwilio = url.includes('api.twilio.com')
                        const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
                        const resolvedUrl = isTwilio
                          ? `${BACKEND_URL}/api/chat/media-proxy?url=${encodeURIComponent(url)}&token=${token}`
                          : url
                        const contentType = node.metadata?.[`MediaContentType${idx}`]?.[0] || ''
                        const isImage = contentType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(url)
                        return (
                          <div key={idx} className='border rounded p-2 bg-white-50 d-inline-block' style={{ maxWidth: 'fit-content' }}>
                            {isImage ? (
                              <a href={resolvedUrl} target='_blank' rel='noopener noreferrer'>
                                <img src={resolvedUrl} alt="Attachment" style={{ maxHeight: '150px', maxWidth: '100%', cursor: 'pointer' }} className='rounded shadow-sm' />
                              </a>
                            ) : (
                              <a href={resolvedUrl} target='_blank' rel='noopener noreferrer' className='d-flex align-items-center gap-2 text-decoration-none'>
                                <i className='bi bi-file-earmark-arrow-down fs-4'></i>
                                <span className='small'>Attachment {idx + 1}</span>
                              </a>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div className='d-flex justify-content-end align-items-center gap-2 mt-2 pt-1 border-top border-black-50 opacity-50'>
                    <span className='x-small' style={{ fontSize: '0.7rem' }}>
                      {moment(node.created).format('hh:mm A')}
                    </span>
                    {isOutbound && (
                      <i className={`bi bi-check2-all x-small ${node.status === 'READ' ? 'text-primary' : ''}`}></i>
                    )}
                  </div>
                </div>

                <div className='d-flex gap-2 px-1' style={{ fontSize: '0.7rem' }}>
                  <span className='text-muted'>
                    {moment(node.created).format('MMM DD')}
                  </span>
                  {node.user && isOutbound && (
                    <span className='text-muted'>
                      • {node.user.firstName} {node.user.lastName}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {recipient && (
        <SendMessageCanvas
          show={isOpenMessaging}
          onHide={onCloseMessaging}
          recipient={recipient}
          context={{
            type: originModule === 'leads' ? 'LEAD' : originModule === 'policies' ? 'POLICY' : 'QUOTE',
            id: originId,
          }}
          data={contextData}
          defaultChannel='WHATSAPP'
          onSuccess={() => refetch()}
        />
      )}
    </div>
  )
}
